import i18next from 'i18next'
import jsep from 'jsep'
import _ from 'lodash'
import { z, ZodType } from 'zod'

import { videoPlayers } from 'features/globalSetup/types/videoPlayer'
import { WorkspacePrebidConfiguration } from 'features/globalSetup/types/workspacePrebidConfiguration'
import { Workspace } from 'features/workspace'
import { JS_OBJECT_EXPRESSION } from 'providers/jsep/constants'
import { MAX_SAFE_GQL_INTEGER } from 'providers/tanstack-query/constants'
import { validateJSONSchema } from 'utils/json'
import { DEFAULT_MAX_RTT, PREBID_VARIABLE_NAME_ALLOWED_CHARACTERS_REGEX } from './constants'
import { consentManagementSchema } from './sections/ConsentManagementSection/components/ConsentManagementCommonFields/schema'

export const allowActivitiesData = z.string().refine(
  (value: string) => {
    try {
      return value.length === 0 || jsep(value).type === JS_OBJECT_EXPRESSION
    } catch {
      return false
    }
  },
  { message: i18next.t('common:form.error.jsObject') },
)
export const auctionDelay: ZodType<number> = z.number().int().min(0).max(2_000)
export const prebidCustomConfig: ZodType<string> = z.string().superRefine(validateJSONSchema)

export const schema = z
  .object({
    allowActivitiesData,
    auctionDelay,
    connectionSpeed: z.object({
      disablePrebidStack: z.boolean().default(false),
      maxRtt: z.number().int().min(0).max(MAX_SAFE_GQL_INTEGER).default(DEFAULT_MAX_RTT),
    }),
    prebidCustomConfig,
    prebidVariableName: z.string().optional(),
    s2sTimeoutMultiplier: z.number().int().min(0).max(100),
    videoPlayer: z.array(z.enum(videoPlayers)),
    workspaceId: z.string(),
  })
  .extend(consentManagementSchema.shape)
  .superRefine(({ prebidVariableName }: Schema, ctx: z.RefinementCtx): void => {
    if (prebidVariableName !== undefined) {
      if (prebidVariableName.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t('common:form.error.isRequired'),
          path: ['prebidVariableName'],
        })
      }

      if (
        prebidVariableName.length > 0 &&
        !PREBID_VARIABLE_NAME_ALLOWED_CHARACTERS_REGEX.test(prebidVariableName)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:defaults.prebid.prebidVariable.prebidVariableName.errors.invalidCharacters',
          ),
          path: ['prebidVariableName'],
        })
      }
    }
  })

export type Schema = z.infer<typeof schema>

export const schemaDefault = (
  workspacePrebidConfiguration: WorkspacePrebidConfiguration,
  workspaceId: Workspace['id'],
): Schema => {
  const {
    connectionSpeed,
    ...fields
  }: Pick<WorkspacePrebidConfiguration, keyof Omit<WorkspacePrebidConfiguration, 'id'>> = _.pick(
    workspacePrebidConfiguration,
    [
      'allowActivitiesData',
      'auctionDelay',
      'gdprTcfEnabled',
      'gdprTcfTimeout',
      'gppControlUsNat',
      'gppControlUsStates',
      'gppEnabled',
      'gppTimeout',
      'prebidTimeout',
      'prebidCustomConfig',
      'connectionSpeed',
      's2sTimeoutMultiplier',
      'usPrivacyCcpaEnabled',
      'usPrivacyCcpaTimeout',
      'videoPlayer',
      'prebidVariableName',
    ],
  )

  return {
    ...fields,
    connectionSpeed: {
      disablePrebidStack: Boolean(connectionSpeed?.disablePrebidStack),
      maxRtt: _.isNil(connectionSpeed.maxRtt) ? DEFAULT_MAX_RTT : connectionSpeed.maxRtt,
    },
    workspaceId,
  }
}
